import { asApiFailure, parseJsonObject, wasReported } from "@utils/apiFailure";
import { NOT_SAVED } from "./editOrganization";

// What a refused PUT locations/{locationId}/address (C6, API #433) means to
// the Edit Location drawer:
// - location_changed: the drawer offers the latest details;
// - status_not_editable, charge_settling (an activation or renewal charge
//   still settling blocks a change of state, not a correction): the drawer
//   says so;
// - certificate: certificate_required / certificate_unexpected, said at the
//   certificate field;
// - gone: the API's own 404, the location or its client is gone;
// - other: anything the drawer does not explain itself (useApi toasted it).
export type EditLocationRefusalKind =
    | "location_changed"
    | "status_not_editable"
    | "charge_settling"
    | "certificate"
    | "gone"
    | "other";

export interface EditLocationRefusal {
    kind: EditLocationRefusalKind;
    message: string;
}

export const LOCATION_CHANGED_MESSAGE =
    "This location changed after you opened it, so load the latest details and make your changes again.";
export const STATUS_NOT_EDITABLE_MESSAGE =
    "This location can't be edited in its current status.";
export const CHARGE_SETTLING_MESSAGE =
    "A payment for this location is still being confirmed, so its state can't change yet, but corrections to the street, city or ZIP still save.";
export const LOCATION_GONE_MESSAGE =
    "This location no longer exists. It may have been deleted.";

// The certificate refusals carry the API's own sentence; these stand in when
// a body carried none.
const CERTIFICATE_FALLBACK = new Map<string, string>([
    [
        "certificate_required",
        "Changing the state needs a resale certificate for the new state.",
    ],
    [
        "certificate_unexpected",
        "A resale certificate goes only with a change of state.",
    ],
]);

// The 409 codes, each with the drawer's own sentence.
const CONFLICTS = new Map<string, EditLocationRefusal>([
    [
        "location_changed",
        { kind: "location_changed", message: LOCATION_CHANGED_MESSAGE },
    ],
    [
        "status_not_editable",
        { kind: "status_not_editable", message: STATUS_NOT_EDITABLE_MESSAGE },
    ],
    [
        "activation_charge_unconfirmed",
        { kind: "charge_settling", message: CHARGE_SETTLING_MESSAGE },
    ],
    [
        "renewal_charge_held",
        { kind: "charge_settling", message: CHARGE_SETTLING_MESSAGE },
    ],
]);

export const editLocationRefusal = (error: unknown): EditLocationRefusal => {
    const failure = asApiFailure(error);

    // The API's own 404 is the one useLocationApi did not toast (it carried
    // the API's body). A 404 useApi did toast (no body, a gateway's page)
    // says nothing about the location, so it is only "not saved".
    if (failure?.status === 404 && !wasReported(error)) {
        return { kind: "gone", message: LOCATION_GONE_MESSAGE };
    }

    const body = parseJsonObject(failure?.detail);
    const code = typeof body?.error === "string" ? body.error : "";
    const apiMessage =
        typeof body?.message === "string" ? body.message.trim() : "";

    const certificateFallback = CERTIFICATE_FALLBACK.get(code);
    if (failure?.status === 400 && certificateFallback) {
        return {
            kind: "certificate",
            message: apiMessage || certificateFallback,
        };
    }

    if (failure?.status !== 409) {
        return { kind: "other", message: NOT_SAVED };
    }

    // A 409 this drawer does not know: the toast was suppressed, so the API's
    // sentence (or ours) is the only word on it.
    return (
        CONFLICTS.get(code) ?? { kind: "other", message: apiMessage || NOT_SAVED }
    );
};
